import { Component, computed, effect, inject, OnDestroy, signal, untracked } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { clpp } from '@castlabs/prestoplay';
import { PlayerService } from '../../data-access/player.service';
import { ITimeLocator } from '../../data-access/binge-item.interface';

@Component({
  selector: 'player-skip-intro',
  imports: [TranslocoPipe],
  templateUrl: './player-skip-intro.component.html',
  styleUrl: './player-skip-intro.component.scss'
})
export class PlayerSkipIntroComponent implements OnDestroy {
  private playerService = inject(PlayerService);

  // Once dismissed (auto-hide or click) the cue stays hidden until a new item plays.
  private hidden = signal(false);
  // The shared player's progress signal keeps the PREVIOUS stream's position (e.g. a
  // cover trailer) until the new item's first timeupdate — off stale progress the cue
  // would show and its auto-hide would fire before the intro is ever reached. Progress
  // is only trusted once it MOVES off the value snapshotted during the current item's
  // load cycle (IDLE/PREPARING/BUFFERING).
  private armed = signal(false);
  private baselineProgress: number | null = null;
  private itemRef: string | null = null;
  private hideTimeout: any = null;

  private intro = computed(() => {
    const locators: ITimeLocator[] | null = this.playerService.getItem()()?.timeLocators;
    const introIn = locators?.find(l => l.type === 'IntroIn')?.location;
    const introOut = locators?.find(l => l.type === 'IntroOut')?.location;
    return introIn != null && introOut != null && introOut > introIn
      ? { in: introIn, out: introOut }
      : null;
  });

  public displaySkipIntro = computed(() => {
    const intro = this.intro();
    if (!intro || this.hidden() || !this.armed()) return false;
    const progress = this.playerService.getProgress()();
    return this.playerService.getDuration() > 0 && progress > intro.in && progress < intro.out;
  });

  constructor() {
    effect(() => {
      const ref = this.playerService.getItem()()?.referenceId ?? null;
      const state = this.playerService.getState()();
      const progress = this.playerService.getProgress()();
      untracked(() => {
        if (ref !== this.itemRef) {
          this.itemRef = ref;
          clearTimeout(this.hideTimeout);
          this.hideTimeout = null;
          this.hidden.set(false);
          this.armed.set(false);
          this.baselineProgress = null;
        }
        if (this.armed()) return;
        if (this.baselineProgress == null &&
          [clpp.Player.State.IDLE, clpp.Player.State.PREPARING, clpp.Player.State.BUFFERING].includes(state)) {
          this.baselineProgress = progress;
        } else if (this.baselineProgress != null && progress !== this.baselineProgress) {
          this.armed.set(true);
        }
      });
    });
    // Parity with the legacy player: the cue auto-hides 5s after it first appears
    // and does not come back if the user seeks into the intro again.
    effect(() => {
      if (this.displaySkipIntro() && this.hideTimeout == null) {
        this.hideTimeout = setTimeout(() => this.hidden.set(true), 5000);
      }
    });
  }

  skipIntro() {
    const intro = this.intro();
    if (!intro) return;
    this.hidden.set(true);
    this.playerService.setPosition(intro.out).subscribe();
  }

  ngOnDestroy() {
    clearTimeout(this.hideTimeout);
  }
}
